<template>
  <transition name="ntf-reader">
    <aside
      v-if="open"
      class="ntf-reader"
      role="dialog"
      aria-modal="false"
      :aria-label="item && item.title ? item.title : 'Notification'"
    >
      <header class="ntf-reader__head">
        <div class="ntf-reader__source" v-if="sourceLabel">
          <span class="ntf-reader__glyph" aria-hidden="true">{{ sourceGlyph }}</span>
          <span class="ntf-reader__source-label">{{ sourceLabel }}</span>
          <span class="ntf-reader__ai" v-if="item && item.source && item.source.aiComposed" title="Written by GNey">AI</span>
        </div>
        <button type="button" class="ntf-reader__close" aria-label="Close" @click="close">
          <font-awesome-icon :icon="['fas', 'times']" />
        </button>
      </header>

      <div class="ntf-reader__body fts-scrollbar">
        <template v-if="loading">
          <fts-spinner show :size="24" class="ntf-reader__spinner" />
        </template>
        <template v-else-if="error">
          <div class="ntf-reader__error">{{ error }}</div>
        </template>
        <template v-else-if="item">
          <h2 class="ntf-reader__title">{{ item.title || 'Notification' }}</h2>
          <div class="ntf-reader__meta">
            <span v-if="item.actor">{{ actorName }}</span>
            <span v-if="item.actor && sentAt"> · </span>
            <fts-tippy v-if="sentAt" :content="sentAtFull" :options="{ placement: 'bottom' }">
              <template #trigger>{{ sentAt }}</template>
            </fts-tippy>
          </div>
          <div v-if="isMarkdown" class="ntf-reader__content ntf-md" v-html="renderMarkdown(item.body)"></div>
          <div v-else-if="item.bodyFormat === 'html'" class="ntf-reader__content ntf-md" v-html="item.body"></div>
          <div v-else class="ntf-reader__content ntf-md ntf-md--text">{{ item.body || item.summary }}</div>
        </template>
      </div>

      <footer class="ntf-reader__foot" v-if="item && !loading && (hasAbout || hasRoutine || gney)">
        <fts-btn v-if="hasAbout" color="primary" @click="openEntry">Open entry</fts-btn>
        <fts-btn v-if="gney" :color="hasAbout ? undefined : 'primary'" :text="hasAbout" @click="askGney">Ask GNey about this</fts-btn>
        <fts-btn v-if="hasRoutine" text @click="openRoutine">Open routine</fts-btn>
      </footer>
    </aside>
  </transition>
</template>

<script>
/**
 * Content-notification reader (docs/notification-content-proposal.md §8.1): the right-rail
 * panel a locus-less notification opens into, instead of the entry Quick View. One instance
 * per shell; opened through eventBus "notificationOpen" with the reading id. The body is the
 * markdown GNey / a routine wrote, rendered by the SAME renderer as the chat (entry tokens →
 * chips) — there is deliberately no second markdown path.
 */
import moment from "moment";
import teammateChatMixin from "../../teammate/teammateChatMixin";
import { clientStore } from "../../store/clientStore";
import commons from "../../commons/commons";
import FTSBtn from "../FTSBtn";
import FTSSpinner from "../FTSSpinner";
import FTSTippy from "../FTSTippy";

const SOURCE_GLYPHS = {
  routine: "⟳",
  teammate: "✦",
  automation: "⚙",
  mcp: "⇄",
  report: "▤",
  system: "ⓘ",
};

export default {
  name: "FTSNotificationReader",
  mixins: [teammateChatMixin],
  components: {
    "fts-btn": FTSBtn,
    "fts-spinner": FTSSpinner,
    "fts-tippy": FTSTippy,
  },
  props: {
    /** True when the GNey dock is mounted in this shell — shows "Ask GNey about this" (D7). */
    gney: { type: Boolean, default: false },
  },
  data() {
    return {
      open: false,
      loading: false,
      error: null,
      readingId: null,
      item: null,
    };
  },
  computed: {
    isMarkdown() {
      return !!this.item && (!this.item.bodyFormat || this.item.bodyFormat === "markdown") && !!this.item.body;
    },
    sourceLabel() {
      const s = this.item && this.item.source;
      if (!s) return "";
      if (s.label) return s.label;
      const kind = String(s.kind || "").toLowerCase();
      if (kind === "teammate") return "GNey";
      return kind ? kind.charAt(0).toUpperCase() + kind.slice(1) : "";
    },
    sourceGlyph() {
      const kind = this.item && this.item.source ? String(this.item.source.kind || "").toLowerCase() : "";
      return SOURCE_GLYPHS[kind] || SOURCE_GLYPHS.system;
    },
    actorName() {
      return this.item && this.item.actor ? String(this.item.actor) : "";
    },
    sentAt() {
      const d = this.item && this.item.audit && this.item.audit.createdDate;
      return d ? commons.momentRelativeTime(d) : "";
    },
    sentAtFull() {
      const d = this.item && this.item.audit && this.item.audit.createdDate;
      return d ? moment(d).format("llll") : "";
    },
    hasAbout() {
      return !!(this.item && this.item.about && this.item.about.locusId);
    },
    hasRoutine() {
      const s = this.item && this.item.source;
      return !!(s && String(s.kind).toLowerCase() === "routine" && s.routineId);
    },
  },
  mounted() {
    this._onOpen = (id) => this.show(id);
    this._onKey = (e) => { if (e.key === "Escape" && this.open) this.close(); };
    window.eventBus.$on("notificationOpen", this._onOpen);
    window.addEventListener("keydown", this._onKey);
  },
  beforeDestroy() {
    window.eventBus.$off("notificationOpen", this._onOpen);
    window.removeEventListener("keydown", this._onKey);
  },
  methods: {
    /** The chat mixin resolves {{moduleId:entryId}} tokens against this list. */
    chatModules() {
      return clientStore.state.workspaceModules || [];
    },
    async show(id) {
      if (!id) return;
      this.readingId = id;
      this.open = true;
      this.loading = true;
      this.error = null;
      this.item = null;
      try {
        const res = await clientStore.dispatch("retrieveNotificationDetail", id);
        this.item = res && res.data ? res.data : null;
        if (!this.item) this.error = "This notification is no longer available.";
        else this.$emit("read", id);
      } catch (err) {
        this.error = err && err.response && err.response.status === 404
          ? "This notification is no longer available."
          : "Could not load the notification. Try again.";
      } finally {
        this.loading = false;
      }
    },
    close() {
      this.open = false;
      this.item = null;
      this.readingId = null;
    },
    openEntry() {
      if (!this.hasAbout) return;
      this.$emit("open-entry", { id: this.item.about.locusId, vectorId: this.item.about.vectorId });
    },
    openRoutine() {
      if (!this.hasRoutine) return;
      this.$emit("open-routine", { routineId: this.item.source.routineId, runId: this.item.source.routineRunId });
      this.close();
    },
    /**
     * Hand the notification to GNey: the dock opens it as the first card of a fresh conversation
     * with the context attached (D11) — server-side grounding by reading id, nothing pasted into
     * the composer. The reader closes: both panels share the right rail.
     */
    askGney() {
      if (!this.item) return;
      window.eventBus.$emit("gney:openNotification", {
        id: this.readingId,
        title: this.item.title || "Notification",
        label: this.sourceLabel,
      });
      this.close();
    },
  },
};
</script>

<style scoped>
.ntf-reader {
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  width: var(--gney-dock-width, 380px);
  max-width: 100vw;
  z-index: 1045;
  display: flex;
  flex-direction: column;
  background: var(--fts-white, #fff);
  border-left: 1px solid var(--fts-border, #e5e7eb);
  box-shadow: -8px 0 24px rgba(0, 0, 0, 0.08);
}
.ntf-reader-enter-active, .ntf-reader-leave-active { transition: transform 0.18s ease, opacity 0.18s ease; }
.ntf-reader-enter, .ntf-reader-leave-to { transform: translateX(24px); opacity: 0; }
@media (prefers-reduced-motion: reduce) {
  .ntf-reader-enter-active, .ntf-reader-leave-active { transition: none; }
}
.ntf-reader__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 0.75rem 0.75rem 0.5rem 1rem;
  border-bottom: 1px solid var(--fts-border, #e5e7eb);
}
.ntf-reader__source {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
  font-size: 0.75rem;
  letter-spacing: 0.02em;
  text-transform: uppercase;
  color: var(--fts-default, #6b7280);
}
.ntf-reader__glyph { font-size: 0.95rem; line-height: 1; }
.ntf-reader__source-label { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.ntf-reader__ai {
  padding: 1px 5px;
  border-radius: 999px;
  border: 1px solid rgba(var(--fts-primary-rgb, 43, 158, 198), 0.45);
  color: var(--fts-primary, #2b9ec6);
  font-size: 0.625rem;
  font-weight: 700;
}
.ntf-reader__close {
  flex: 0 0 auto;
  width: 28px;
  height: 28px;
  border: 0;
  border-radius: 999px;
  background: transparent;
  color: var(--fts-default, #6b7280);
  cursor: pointer;
}
.ntf-reader__close:hover, .ntf-reader__close:focus-visible { background: rgba(var(--fts-gray-100-rgb, 243, 244, 246), 0.9); outline: none; }
.ntf-reader__body { flex: 1 1 auto; min-height: 0; overflow-y: auto; padding: 1rem 1rem 1.25rem; }
.ntf-reader__spinner { display: flex; justify-content: center; padding: 2rem 0; }
.ntf-reader__error { color: var(--fts-default, #6b7280); font-size: 0.875rem; }
.ntf-reader__title {
  margin: 0 0 0.25rem;
  font-size: 1.125rem;
  font-weight: 600;
  line-height: 1.4;
  text-wrap: balance;
}
.ntf-reader__meta { margin-bottom: 1rem; font-size: 0.75rem; color: var(--fts-default, #6b7280); }
.ntf-reader__content { font-size: 0.875rem; line-height: 1.55; overflow-wrap: anywhere; }
.ntf-md--text { white-space: pre-wrap; }
/* markdown blocks — same rhythm as the chat's .ct-md */
.ntf-md :deep(p) { margin: 0 0 8px; }
.ntf-md :deep(p:last-child) { margin-bottom: 0; }
.ntf-md :deep(h1), .ntf-md :deep(h2), .ntf-md :deep(h3), .ntf-md :deep(h4) { margin: 12px 0 6px; font-size: 0.95rem; font-weight: 600; }
.ntf-md :deep(ul), .ntf-md :deep(ol) { margin: 4px 0 8px; padding-left: 20px; }
.ntf-md :deep(li) { margin: 6px 0; }
.ntf-md :deep(blockquote) { margin: 8px 0; padding: 6px 10px; border-left: 3px solid rgba(var(--fts-primary-rgb, 43, 158, 198), 0.4); background: rgba(var(--fts-primary-rgb, 43, 158, 198), 0.06); }
.ntf-md :deep(code) { padding: 1px 4px; border-radius: 4px; background: rgba(var(--fts-primary-rgb, 43, 158, 198), 0.1); font-size: 0.8125rem; }
.ntf-md :deep(pre) { overflow-x: auto; padding: 8px; border-radius: 6px; background: #1f2330; color: #e6e8ec; }
.ntf-md :deep(pre code) { background: transparent; color: inherit; padding: 0; }
.ntf-md :deep(table) { border-collapse: collapse; width: 100%; display: block; overflow-x: auto; font-variant-numeric: tabular-nums; }
.ntf-md :deep(th), .ntf-md :deep(td) { padding: 4px 8px; border: 1px solid var(--fts-border, #e5e7eb); text-align: left; }
.ntf-md :deep(th) { background: rgba(var(--fts-gray-100-rgb, 243, 244, 246), 0.9); font-weight: 600; }
.ntf-reader__foot {
  display: flex;
  gap: 8px;
  padding: 0.625rem 1rem;
  border-top: 1px solid var(--fts-border, #e5e7eb);
}
</style>
